import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  TextField,
  IconButton,
  Divider,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Collapse,
  Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import axios from "axios";
import { BACKEND_URL } from "../../utils/constants";

const BLANK_WEAPON = {
  name: "",
  display_name: "",
  charges: "",
  angles: ["la", "ha"],
  projectiles: [{ label: "HE", table: "", solver: "tabular" }],
};

const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 1.25 } };

export default function WeaponsPanel() {
  const [weapons, setWeapons] = useState([]);
  const [msg, setMsg] = useState(null); // {sev, text}
  const [showAdd, setShowAdd] = useState(false);
  const [w, setW] = useState(BLANK_WEAPON);
  const [imp, setImp] = useState({ weapon: "", table: "", charge: "", json: "" });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/weapons`);
      setWeapons(r.data?.weapons || []);
    } catch (e) {
      setMsg({ sev: "error", text: "Failed to load weapons." });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setProj = (i, key, val) =>
    setW((p) => ({ ...p, projectiles: p.projectiles.map((pr, j) => (j === i ? { ...pr, [key]: val } : pr)) }));
  const addProjRow = () =>
    setW((p) => ({ ...p, projectiles: [...p.projectiles, { label: "", table: "", solver: "tabular" }] }));
  const rmProjRow = (i) =>
    setW((p) => ({ ...p, projectiles: p.projectiles.filter((_, j) => j !== i) }));
  const toggleAngle = (a) =>
    setW((p) => ({ ...p, angles: p.angles.includes(a) ? p.angles.filter((x) => x !== a) : [...p.angles, a] }));

  const saveWeapon = async () => {
    try {
      await axios.post(`${BACKEND_URL}/weapons`, {
        name: w.name,
        display_name: w.display_name,
        charges: w.charges.split(",").map((s) => s.trim()).filter(Boolean),
        angles: w.angles,
        projectiles: w.projectiles.filter((p) => p.label && p.table),
      });
      setMsg({ sev: "success", text: `Saved ${w.name.toUpperCase()}.` });
      setW(BLANK_WEAPON);
      setShowAdd(false);
      load();
    } catch (e) {
      setMsg({ sev: "error", text: e?.response?.data?.message || "Failed to save weapon." });
    }
  };

  const removeWeapon = async (name) => {
    try {
      await axios.delete(`${BACKEND_URL}/weapons/${encodeURIComponent(name)}`);
      setMsg({ sev: "success", text: `Removed ${name}.` });
      load();
    } catch (e) {
      setMsg({ sev: "error", text: e?.response?.data?.message || "Failed to remove." });
    }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rd = new FileReader();
    rd.onload = () => setImp((p) => ({ ...p, json: String(rd.result || "") }));
    rd.readAsText(file);
  };

  const importTable = async () => {
    let data;
    try {
      data = JSON.parse(imp.json);
    } catch {
      setMsg({ sev: "error", text: "Table content is not valid JSON." });
      return;
    }
    try {
      const r = await axios.post(`${BACKEND_URL}/weapons/${encodeURIComponent(imp.weapon)}/tables`, {
        table: imp.table, charge: imp.charge, data,
      });
      setMsg({ sev: "success", text: `Imported ${r.data?.file}.` });
      setImp((p) => ({ ...p, json: "" }));
    } catch (e) {
      setMsg({ sev: "error", text: e?.response?.data?.message || "Import failed." });
    }
  };

  const impWeapon = weapons.find((x) => x.name === imp.weapon);

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography sx={{ fontWeight: 700 }}>Weapons &amp; Tables</Typography>
        <Chip size="small" label={`${weapons.length} gun type${weapons.length === 1 ? "" : "s"}`} />
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setShowAdd((s) => !s)}>
          Add gun type
        </Button>
      </Stack>

      {msg && <Alert severity={msg.sev} sx={{ mb: 1 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      {/* Registered weapons */}
      <Stack spacing={0.75} sx={{ mb: 1 }}>
        {weapons.map((x) => (
          <Box key={x.name} sx={{ p: 1, border: "1px solid", borderColor: "divider", borderRadius: 1.25 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 700 }}>{x.name}</Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>{x.display_name}</Typography>
              {x.builtin && <Chip size="small" label="built-in" />}
              <Box sx={{ flex: 1 }} />
              {!x.builtin && (
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => removeWeapon(x.name)}>
                    <DeleteOutlineRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
              {(x.projectiles || []).map((p) => (
                <Chip key={p.label} size="small" variant="outlined"
                      label={`${p.label} → ${p.table} (${p.solver})`} sx={{ fontFamily: "monospace", fontSize: 11 }} />
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
              charges: {(x.charges || []).join(", ") || "—"} · angles: {(x.angles || []).join("/")}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Add gun type */}
      <Collapse in={showAdd} unmountOnExit>
        <Box sx={{ p: 1.25, border: "1px dashed", borderColor: "divider", borderRadius: 1.25, mb: 1 }}>
          <Stack spacing={1}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
              <TextField size="small" label="Name (id)" placeholder="M777" value={w.name}
                         onChange={(e) => setW((p) => ({ ...p, name: e.target.value }))} sx={fieldSx} />
              <TextField size="small" fullWidth label="Display name" placeholder="M777 155 mm"
                         value={w.display_name} onChange={(e) => setW((p) => ({ ...p, display_name: e.target.value }))} sx={fieldSx} />
            </Stack>
            <TextField size="small" fullWidth label="Charges (comma separated)" placeholder="1, 2, 3, 4, 5"
                       value={w.charges} onChange={(e) => setW((p) => ({ ...p, charges: e.target.value }))} sx={fieldSx} />
            <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>PROJECTILES</Typography>
            {w.projectiles.map((p, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="center">
                <TextField size="small" label="Label" placeholder="HE" value={p.label}
                           onChange={(e) => setProj(i, "label", e.target.value)} sx={{ ...fieldSx, width: 110 }} />
                <TextField size="small" label="Table id" placeholder="HE_M795" value={p.table}
                           onChange={(e) => setProj(i, "table", e.target.value)} sx={{ ...fieldSx, flex: 1 }} />
                <FormControl size="small" sx={{ width: 130 }}>
                  <InputLabel>Solver</InputLabel>
                  <Select label="Solver" value={p.solver} onChange={(e) => setProj(i, "solver", e.target.value)}>
                    <MenuItem value="tabular">tabular</MenuItem>
                    <MenuItem value="illum">illum</MenuItem>
                  </Select>
                </FormControl>
                <IconButton size="small" onClick={() => rmProjRow(i)} disabled={w.projectiles.length <= 1}>
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" startIcon={<AddRoundedIcon />} onClick={addProjRow}>Projectile</Button>
              <Box sx={{ flex: 1 }} />
              {["la", "ha"].map((a) => (
                <Chip key={a} size="small" label={a === "la" ? "Low angle" : "High angle"}
                      color={w.angles.includes(a) ? "primary" : "default"}
                      variant={w.angles.includes(a) ? "filled" : "outlined"} onClick={() => toggleAngle(a)} />
              ))}
            </Stack>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" size="small" onClick={saveWeapon}
                      disabled={!w.name.trim() || !w.projectiles.some((p) => p.label && p.table)}>
                Save gun type
              </Button>
            </Box>
          </Stack>
        </Box>
      </Collapse>

      <Divider sx={{ my: 1 }} />

      {/* Import a firing table */}
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>IMPORT FIRING TABLE (JSON)</Typography>
      <Stack spacing={1} sx={{ mt: 0.5 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Weapon</InputLabel>
            <Select label="Weapon" value={imp.weapon}
                    onChange={(e) => setImp((p) => ({ ...p, weapon: e.target.value, table: "" }))}>
              {weapons.map((x) => <MenuItem key={x.name} value={x.name}>{x.name}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }} disabled={!impWeapon}>
            <InputLabel>Table (projectile)</InputLabel>
            <Select label="Table (projectile)" value={imp.table}
                    onChange={(e) => setImp((p) => ({ ...p, table: e.target.value }))}>
              {(impWeapon?.projectiles || []).map((p) => (
                <MenuItem key={p.table} value={p.table}>{p.label} → {p.table}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" label="Charge" placeholder="4" value={imp.charge}
                     onChange={(e) => setImp((p) => ({ ...p, charge: e.target.value }))} sx={{ ...fieldSx, width: 110 }} />
          <Button component="label" size="small" startIcon={<UploadFileRoundedIcon />}>
            File
            <input hidden type="file" accept=".json,application/json" onChange={onFile} />
          </Button>
        </Stack>
        <TextField size="small" multiline minRows={3} maxRows={10} label="Table JSON (paste or load a file)"
                   value={imp.json} onChange={(e) => setImp((p) => ({ ...p, json: e.target.value }))}
                   sx={{ ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 11 } }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="contained" size="small" onClick={importTable}
                  disabled={!imp.weapon || !imp.table || !imp.charge.trim() || !imp.json.trim()}>
            Import table
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
